import clsx from 'clsx';
import { forwardRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../../../core/hooks/useId.js';
import { AriaDisabledProps } from '../../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../../core/types/data-props.js';
import { DOMProps } from '../../../../core/types/dom.js';
import { MaskingProps } from '../../../../core/types/masking-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../../core/utils/can-receive-default-text-format.js';
import { mergeProps } from '../../../../core/utils/merge-props.js';
import { Text } from '../../../../typography/text/Text.js';
import { useFormFieldContext } from '../../hooks/useFormFieldContext.js';
import * as styles from './Label.sty.js';

const COMPONENT_NAME = 'Label';

/**
 * Available options for the label size
 * @public
 */
export interface LabelProps
  extends
    WithChildren,
    DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaDisabledProps,
    BehaviorTrackingProps {
  /** Defines the id of a labelable form related field. */
  htmlFor?: string;

  /**
   * If there is a wrapping form field then this label uses the required value
   * from the form field, otherwise it uses this required value. The label
   * has a visual style to indicate the associated field is a mandatory field.
   * @defaultValue false
   */
  required?: boolean;

  /**
   * If there is a wrapping form field then this label uses the disabled value
   * from the form field, otherwise it uses this disabled value. The label
   * has a visual style to indicate the associated field is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
}

const SimpleLabelText = forwardRef<HTMLLabelElement, LabelProps>(
  (props, forwardedRef) => (
    <Text
      ref={forwardedRef}
      as="label"
      textStyle="small-emphasized"
      {...props}
    />
  ),
);

/**
 * The `Label` component allows users to add an associated text to another
 * form control by using the `FormField` component.
 * @public
 */
export const Label = /* @__PURE__ */ forwardRef<HTMLLabelElement, LabelProps>(
  (props, forwardedRef) => {
    const {
      id,
      children,
      htmlFor,
      disabled: propDisabled,
      required: propRequired,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': ariaDisabledProp,
      ...remainingProps
    }: LabelProps = props;
    const generatedId = useId();

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const {
      // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
      controlId,
      formFieldId,
      required,
      disabled,
      ariaDisabled,
      readOnly,
      formControlId,
      registerFormFieldLabelId,
    } = useFormFieldContext();

    const labelId = id ?? (formFieldId ? generatedId : undefined);

    useEffect(() => {
      if (labelId === undefined) {
        return;
      }

      return registerFormFieldLabelId(labelId);
    }, [labelId, registerFormFieldLabelId]);

    const isStringChild = _canReceiveDefaultTextFormat(children);
    const Component = isStringChild ? SimpleLabelText : 'label';

    const labelFor = htmlFor || formControlId || formFieldId || controlId;

    return (
      <Component
        htmlFor={labelFor}
        id={labelId}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        className={clsx(
          styles.label({
            disabled: disabled || propDisabled || ariaDisabled,
            // Only apply the required styles if we have string children, otherwise block level
            // elements in children will move the `*` required marker somewhere.
            required: isStringChild && (required ?? propRequired),
            readOnly,
          }),
          consumerClassName,
        )}
        style={consumerStyle}
        {...mergeProps(restProps, behavioralTrackingProps)}
        data-dt-component={COMPONENT_NAME}
      >
        {children}
        {isStringChild && readOnly && (
          <Text textStyle="small" className={clsx(styles.readOnlyLabel)}>
            <FormattedMessage
              defaultMessage="(read only)"
              id="hIBEZw/eT6Nb+dz3"
              description="Text appended to a label when the associated form field is read-only."
            />
          </Text>
        )}
      </Component>
    );
  },
);
(Label as typeof Label & { displayName: string }).displayName = COMPONENT_NAME;
